/**
 * Preview Panel
 * Shows contextual mockups of how the ad will appear on different platforms
 */

import React, { useMemo, useState } from 'react';
import { Monitor, Smartphone, Eye } from 'lucide-react';
import { useEditorStore, useTemplateStore } from '@/stores';
import { getMockupsForFormat, MockupRenderer } from '@/components/mockups';
import type { MockupConfig } from '@/components/mockups';
import { LayerRenderer } from '@/components/canvas/LayerRenderer';
import { getCanvasBackgroundCSS } from '@/types/template';

export function PreviewPanel() {
  const { selectedFormat } = useEditorStore();
  const { currentTemplate, selectedLayerIds, variableValues } = useTemplateStore();

  // Get available mockups for current format
  const availableMockups = useMemo(
    () => getMockupsForFormat(selectedFormat),
    [selectedFormat]
  );

  // Selected mockup state
  const [selectedMockupId, setSelectedMockupId] = useState<string | null>(
    availableMockups[0]?.id ?? null
  );

  // Update selection when format changes
  React.useEffect(() => {
    if (availableMockups.length > 0 && !availableMockups.find(m => m.id === selectedMockupId)) {
      setSelectedMockupId(availableMockups[0].id);
    }
  }, [availableMockups, selectedMockupId]);

  const selectedMockup = availableMockups.find(m => m.id === selectedMockupId);

  // Create a format object for the preview
  const previewFormat = useMemo(() => ({
    id: 'preview',
    name: 'Preview',
    width: selectedFormat.width,
    height: selectedFormat.height,
    provider: 'custom' as const,
    platforms: '',
  }), [selectedFormat.width, selectedFormat.height]);

  if (!currentTemplate) {
    return (
      <div className="flex items-center justify-center h-full text-fg-muted">
        <div className="text-center">
          <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>Select a template to see previews</p>
        </div>
      </div>
    );
  }

  if (availableMockups.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-fg-muted">
        <div className="text-center">
          <Monitor className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No platform previews available for this format</p>
          <p className="text-xs mt-1 opacity-70">{selectedFormat.name} ({selectedFormat.width}x{selectedFormat.height})</p>
        </div>
      </div>
    );
  }

  // Calculate scale for the ad preview inside mockup
  const maxPreviewWidth = 400;
  const scale = Math.min(1, maxPreviewWidth / selectedFormat.width);

  const bgStyle = getCanvasBackgroundCSS(currentTemplate.canvas.background);

  return (
    <div className="flex gap-6 h-full">
      {/* Mockup selector */}
      <div className="w-48 flex-shrink-0 space-y-2">
        <h4 className="text-xs text-fg-muted uppercase tracking-wider mb-3">Platform Preview</h4>
        {availableMockups.map((mockup) => (
          <button
            key={mockup.id}
            onClick={() => setSelectedMockupId(mockup.id)}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
              selectedMockupId === mockup.id
                ? 'bg-primary/20 text-fg border border-primary/30'
                : 'text-fg-muted hover:bg-white/5 hover:text-fg'
            }`}
          >
            {mockup.platform === 'Mooncloak' ? (
              <Smartphone size={14} />
            ) : (
              <Monitor size={14} />
            )}
            <span>{mockup.name}</span>
          </button>
        ))}

        {/* Format info */}
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="text-xs text-fg-subtle">
            <div className="mb-1">Current Format:</div>
            <div className="text-fg font-medium">{selectedFormat.name}</div>
            <div className="mt-1">{selectedFormat.width} × {selectedFormat.height}px</div>
          </div>
        </div>
      </div>

      {/* Mockup preview */}
      <div className="flex-1 flex items-center justify-center overflow-auto p-4">
        {selectedMockup && (
          <div className="transform-gpu" style={{ transform: 'scale(0.85)', transformOrigin: 'center center' }}>
            <MockupRenderer
              mockup={selectedMockup}
              adWidth={selectedFormat.width}
              adHeight={selectedFormat.height}
            >
              {/* Rendered ad */}
              <div
                style={{
                  width: selectedFormat.width * scale,
                  height: selectedFormat.height * scale,
                  background: bgStyle,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: selectedFormat.width,
                    height: selectedFormat.height,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                  }}
                >
                  <LayerRenderer
                    layers={currentTemplate.layers}
                    format={previewFormat}
                    variableValues={variableValues}
                    selectedLayerIds={[]}
                    hoveredLayerId={null}
                  />
                </div>
              </div>
            </MockupRenderer>
          </div>
        )}
      </div>

      {/* Tips */}
      <div className="w-56 flex-shrink-0">
        <h4 className="text-xs text-fg-muted uppercase tracking-wider mb-3">Preview Tips</h4>
        <div className="space-y-3 text-xs text-fg-subtle">
          <div className="p-3 bg-white/5 rounded-lg">
            <div className="font-medium text-fg mb-1">Contextual Preview</div>
            <p>See how your ad will appear in real platform contexts like feeds, stories, and search results.</p>
          </div>
          <div className="p-3 bg-white/5 rounded-lg">
            <div className="font-medium text-fg mb-1">Multiple Platforms</div>
            <p>Some formats work across multiple platforms. Check each to ensure your design looks great everywhere.</p>
          </div>
          <div className="p-3 bg-white/5 rounded-lg">
            <div className="font-medium text-fg mb-1">Text Legibility</div>
            <p>Ensure your text is readable at the size it will appear on the platform.</p>
          </div>
          <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg mt-4">
            <p className="text-amber-200/80">These mockups are approximations for visualization purposes only. Actual platform appearance may vary.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
